import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DataTable from "react-data-table-component";
import { FaLaptopMedical, FaSearch } from "react-icons/fa";
import { FiLock, FiUnlock } from "react-icons/fi";
import { Tooltip } from "react-tooltip";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import DateRangeFilter from "../../../Components/DateRangeFilter/DateRangeFilter";
import RenewButton from "../../../Components/RenewButton/RenewButton";
import "../Licenses/License.css";

// Derive a human-friendly status from expires_at (matches the dashboard's 3-day rule).
const getExpiryStatus = (expiresAt) => {
  if (!expiresAt) return { label: "-", className: "" };
  const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return { label: "Expired", className: "expired" };
  if (days <= 3) return { label: "Expiring Soon", className: "expiring" };
  return { label: "Active", className: "active" };
};

export default function ProductLicensesTable({ product }) {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [status, setStatus] = useState("");
  const [locked, setLocked] = useState("");
  const [createdRange, setCreatedRange] = useState([null, null]);
  const [expiresRange, setExpiresRange] = useState([null, null]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(true);

  // Serialize date ranges so they can be used in params and dependency arrays.
  const createdFrom = createdRange[0] ? createdRange[0].toISOString() : "";
  const createdTo = createdRange[1] ? createdRange[1].toISOString() : "";
  const expiresFrom = expiresRange[0] ? expiresRange[0].toISOString() : "";
  const expiresTo = expiresRange[1] ? expiresRange[1].toISOString() : "";

  const hasActiveFilters =
    status || locked || search || createdRange[0] || expiresRange[0];

  const clearFilters = () => {
    setStatus("");
    setLocked("");
    setCreatedRange([null, null]);
    setExpiresRange([null, null]);
    setSearchInput("");
    setSearch("");
  };

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset paging whenever the product or any filter changes.
  useEffect(() => {
    setPage(1);
  }, [product, status, locked, createdFrom, createdTo, expiresFrom, expiresTo]);

  const columns = [
    {
      // Same named button as the full Licenses page, so renewing looks and
      // reads identically wherever a license is listed.
      name: "Actions",
      sortable: false,
      minWidth: "190px",
      cell: (row) => (
        <div className="license-actions">
          <RenewButton
            expiresAt={row.expires_at}
            onClick={() => navigate(`/User/RequestQuote`, { state: { extendLicenses: [row] } })}
          />
          <FaLaptopMedical
            data-tooltip-id="product-licenses-tooltip"
            data-tooltip-content="view attached machines"
            data-tooltip-place="bottom"
            className="action-icon"
            onClick={() => navigate(`/user/licenses/${row.license_reference_id}`)}
          />
        </div>
      ),
    },
    {
      name: "License Id",
      selector: (row) => row.license_reference_id,
      sortField: "l.license_reference_id",
      sortable: true,
      minWidth: "180px",
      cell: (row) => <div className="cell-text">{row.license_reference_id}</div>,
    },
    {
      name: "In Use",
      selector: (row) => row.locked,
      sortField: "l.locked",
      sortable: true,
      minWidth: "150px",
      cell: (row) => (
        <div className="status-wrapper">
          {row.locked ? (
            <span className="status-badge in-use">
              <FiLock className="status-icon" /> In Use
            </span>
          ) : (
            <span className="status-badge available">
              <FiUnlock className="status-icon" /> Available
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Last User",
      selector: (row) => row.machine_user,
      sortField: "am.machine_user",
      sortable: true,
      minWidth: "150px",
      cell: (row) => <div className="cell-text">{row.machine_user || "-"}</div>,
    },
    {
      name: "Status",
      selector: (row) => row.expires_at,
      sortField: "l.expires_at",
      sortable: true,
      minWidth: "150px",
      cell: (row) => {
        const s = getExpiryStatus(row.expires_at);
        return <span className={`expiry-pill ${s.className}`}>{s.label}</span>;
      },
    },
    {
      name: "Last Seen",
      selector: (row) => row.last_seen,
      sortField: "am.last_seen",
      sortable: true,
      minWidth: "180px",
      cell: (row) => <div className="cell-text">{row.last_seen ? ShowTime(row.last_seen) : "-"}</div>,
    },
    {
      name: "Created at",
      selector: (row) => row.created_at,
      sortField: "l.created_at",
      sortable: true,
      minWidth: "180px",
      cell: (row) => <div className="cell-text">{ShowTime(row.created_at)}</div>,
    },
    {
      name: "Expires at",
      selector: (row) => row.expires_at,
      sortField: "l.expires_at",
      sortable: true,
      minWidth: "180px",
      cell: (row) => <div className="cell-text">{ShowTime(row.expires_at)}</div>,
    },
  ];

  const getList = async () => {
    try {
      setPending(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/license/index`, {
        params: {
          limit: pageSize,
          page,
          sort_by: sortBy,
          sort_order: sortOrder,
          product,
          status: status || undefined,
          search: search || undefined,
          locked: locked || undefined,
          created_from: createdFrom || undefined,
          created_to: createdTo || undefined,
          expires_from: expiresFrom || undefined,
          expires_to: expiresTo || undefined,
        },
        headers: { "access-token": localStorage.getItem("accessToken") },
      });
      setPending(false);
      if (response.data.status) {
        setTotalRecords(response.data.meta.pagination.total);
        setTableData(response.data.licenses);
      } else {
        toast.error("Something Went Wrong");
      }
    } catch (error) {
      setPending(false);
      toast.error(error.response?.data?.message || "Something Went Wrong");
    }
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder, product, status, search, locked, createdFrom, createdTo, expiresFrom, expiresTo]);

  return (
    <div className="product-licenses">
      <div className="licenses-toolbar">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by license id…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="expiring">Expiring Soon</option>
          <option value="expired">Expired</option>
        </select>
        {/* <select
          className="filter-select"
          value={locked}
          onChange={(e) => setLocked(e.target.value)}
        >
          <option value="">In use: any</option>
          <option value="1">In Use</option>
          <option value="0">Available</option>
        </select> */}
        <div className="date-filter">
          <DateRangeFilter
            startDate={createdRange[0]}
            endDate={createdRange[1]}
            onChange={(dates) => setCreatedRange(dates)}
            placeholderText="Created date"
          />
        </div>
        <div className="date-filter">
          <DateRangeFilter
            startDate={expiresRange[0]}
            endDate={expiresRange[1]}
            onChange={(dates) => setExpiresRange(dates)}
            placeholderText="Expiry date"
          />
        </div>
        {hasActiveFilters && (
          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <div className="table-wrapper">
        <DataTable
          columns={columns}
          data={tableData}
          dense
          direction="auto"
          highlightOnHover
          noHeader
          persistTableHead
          pointerOnHover
          responsive
          progressPending={pending}
          progressComponent={<CustomLoader />}
          pagination
          paginationServer
          paginationPerPage={pageSize}
          paginationTotalRows={totalRecords}
          paginationRowsPerPageOptions={[10, 20, 50, 100, 150]}
          onChangePage={setPage}
          onChangeRowsPerPage={(rows, currentPage) => {
            setPageSize(rows);
            setPage(currentPage);
          }}
          sortServer
          onSort={(col, dir) => {
            setSortBy(col.sortField);
            setSortOrder(dir);
          }}
        />
      </div>
      <Tooltip id="product-licenses-tooltip" style={{ zIndex: 99 }} />
    </div>
  );
}
